// Import de documents dans la bibliothèque : plusieurs fichiers, métadonnées communes, puis suivi du traitement de chaque fichier.
import { useEffect, useRef, useState, type DragEvent, type FormEvent, type KeyboardEvent } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, FileUp, Plus, Trash2, Upload, X, XCircle } from 'lucide-react';
import { api, messageErreur, ErreurApi } from '../../lib/api.ts';
import { useSession } from '../../lib/session.tsx';
import { pluriel } from '../../lib/format.ts';
import {
  Alerte, Badge, Bouton, Champ, ChampChoix, ChampTexte, EntetePage, EtatVide, FilContexte, Panneau, Progression,
} from '../../components/ui.tsx';
import { BadgeStatutDocument, BadgeStatutTache, PROTOCOLES } from '../../components/statuts.tsx';
import { LANGUES, NavDocumentation, TYPES_BIBLIOTHEQUE, TYPES_DOCUMENT, tailleFichier } from './commun.tsx';

const TAILLE_MAX = 200 * 1024 * 1024;
const FORMATS = '.pdf,.png,.jpg,.jpeg,.webp,.xlsx,.csv,.txt';
const DROITS_PROPOSES = [
  'Document public du fabricant — usage interne autorisé',
  'Espace partenaire — usage interne autorisé par le contrat partenaire',
  'Remis par le fabricant ou le distributeur pour nos installations',
  'Fourni par le client pour ses installations',
];

interface Resultat { nom: string; documentId?: string; versionId?: string; doublon?: boolean; tacheId?: string | null; titre?: string; erreur: string | null }

export function PageImporter() {
  const { peut } = useSession();
  const [params] = useSearchParams();
  const qc = useQueryClient();
  const entree = useRef<HTMLInputElement>(null);
  const [fichiers, setFichiers] = useState<File[]>([]);
  const [survol, setSurvol] = useState(false);
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [erreursChamps, setErreursChamps] = useState<Record<string, string>>({});
  const [resultats, setResultats] = useState<Resultat[] | null>(null);

  const [titre, setTitre] = useState('');
  const [docType, setDocType] = useState(params.get('type') && TYPES_DOCUMENT[params.get('type')!] ? params.get('type')! : '');
  const [fabricant, setFabricant] = useState(params.get('fabricant') ?? '');
  const [gamme, setGamme] = useState(params.get('gamme') ?? '');
  const [refs, setRefs] = useState<string[]>(params.get('ref') ? [params.get('ref')!.trim()] : []);
  const [refSaisie, setRefSaisie] = useState('');
  const [versionsCouvertes, setVersionsCouvertes] = useState('');
  const [protocoles, setProtocoles] = useState<string[]>([]);
  const [langue, setLangue] = useState('fr');
  const [region, setRegion] = useState('');
  const [provenance, setProvenance] = useState('');
  const [droitsChoix, setDroitsChoix] = useState('');
  const [droitsLibre, setDroitsLibre] = useState('');
  const [publication, setPublication] = useState('');
  const [libelleVersion, setLibelleVersion] = useState('');

  const fabricants = useQuery({
    queryKey: ['referentiel-fabricants'], enabled: peut('site.lire'),
    queryFn: () => api.get<{ nom: string }[]>('/api/referentiel/fabricants'), staleTime: 300_000,
  });

  if (!peut('doc.importer')) {
    return (
      <div className="pile-l doc-module">
        <EntetePage titre="Importer des documents" />
        <Panneau><EtatVide titre="Accès non autorisé" texte="Vos droits ne permettent pas d’importer des documents. Demandez à un référent technique ou à l’administrateur." /></Panneau>
      </div>
    );
  }

  const ajouterFichiers = (liste: FileList | File[] | null) => {
    if (!liste) return;
    // Copie immédiate : la FileList de l'input est vidée juste après (pour pouvoir re-choisir le même fichier).
    const nouveaux = Array.from(liste);
    setResultats(null);
    setFichiers((avant) => {
      const tous = [...avant];
      for (const f of nouveaux) if (!tous.some((x) => x.name === f.name && x.size === f.size)) tous.push(f);
      return tous.slice(0, 20);
    });
  };
  const deposer = (e: DragEvent) => { e.preventDefault(); setSurvol(false); ajouterFichiers(e.dataTransfer.files); };

  const ajouterRef = () => {
    const nouvelles = refSaisie.split(/[,;\n]/).map((r) => r.trim()).filter(Boolean);
    if (nouvelles.length) setRefs((l) => [...new Set([...l, ...nouvelles])].slice(0, 100));
    setRefSaisie('');
  };
  const toucheRef = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === ';') { e.preventDefault(); ajouterRef(); }
  };

  const droits = droitsChoix === 'autre' ? droitsLibre.trim() : droitsChoix;

  const envoyer = async (e: FormEvent) => {
    e.preventDefault();
    const err: Record<string, string> = {};
    if (!fichiers.length) err.fichiers = 'Ajoutez au moins un fichier.';
    if (!docType) err.docType = 'Choisissez le type de document.';
    if (provenance.trim().length < 3) err.provenance = 'Indiquez d’où vient le document (site du fabricant, distributeur, client…).';
    if (droits.length < 3) err.rights = 'Précisez les conditions d’utilisation : seules les sources autorisées sont collectées.';
    const trop = fichiers.filter((f) => f.size > TAILLE_MAX);
    if (trop.length) err.fichiers = `${trop.map((f) => f.name).join(', ')} : au-delà de 200 Mo.`;
    setErreursChamps(err);
    if (Object.keys(err).length) { setErreur('Certaines informations manquent : voir les champs signalés.'); return; }
    const refsFinales = refSaisie.trim() ? [...new Set([...refs, ...refSaisie.split(/[,;]/).map((r) => r.trim()).filter(Boolean)])] : refs;
    const meta = {
      title: fichiers.length === 1 && titre.trim() ? titre.trim() : null, docType, manufacturer: fabricant.trim() || null, rangeName: gamme.trim() || null,
      productRefs: refsFinales, versionsCovered: versionsCouvertes.trim() || null, protocols: protocoles, language: langue || null,
      region: region.trim() || null, provenance: provenance.trim(), rights: droits, publishedOn: publication || null, versionLabel: libelleVersion.trim() || null,
    };
    const fd = new FormData();
    fd.append('meta', JSON.stringify(meta)); // en tête : le serveur traite chaque fichier dès sa réception
    for (const f of fichiers) fd.append('fichiers', f, f.name);
    setEnvoi(true); setErreur(null);
    try {
      const r = await api.envoyer<{ resultats: Resultat[] }>('/api/documents', fd);
      setResultats(r.resultats);
      setFichiers([]);
      setRefSaisie(''); setRefs(refsFinales);
      qc.invalidateQueries({ queryKey: ['documents'] });
    } catch (ex) {
      if (ex instanceof ErreurApi && Array.isArray(ex.details)) {
        const d: Record<string, string> = {};
        for (const x of ex.details as { champ: string; message: string }[]) d[x.champ] = x.message;
        setErreursChamps(d);
      }
      setErreur(messageErreur(ex));
    } finally { setEnvoi(false); }
  };

  return (
    <div className="pile-l doc-module">
      <EntetePage fil={<FilContexte noeuds={[{ type: 'Documentation', libelle: 'Bibliothèque', to: '/documentation' }, { type: '', libelle: 'Importer' }]} />}
        titre="Importer des documents"
        sousTitre="Plusieurs fichiers peuvent partager les mêmes informations. Chaque fichier est contrôlé, dédoublonné puis lu en arrière-plan." />
      <NavDocumentation peutImporter />

      {resultats && <SuiviImport resultats={resultats} />}

      <form className="pile-l" onSubmit={envoyer} noValidate>
        <Panneau titre="Fichiers">
          <div className={`doc-depot ${survol ? 'doc-depot-survol' : ''}`} onDragOver={(e) => { e.preventDefault(); setSurvol(true); }}
            onDragLeave={() => setSurvol(false)} onDrop={deposer}>
            <FileUp aria-hidden className="doc-depot-icone" />
            <p>Glissez les fichiers ici, ou</p>
            <Bouton variante="secondaire" icone={<Plus aria-hidden />} onClick={() => entree.current?.click()}>Choisir des fichiers</Bouton>
            <input ref={entree} type="file" multiple accept={FORMATS} hidden
              onChange={(e) => { ajouterFichiers(e.target.files); e.target.value = ''; }} />
            <p className="tres-petit discret">PDF, images (PNG, JPEG, WebP), classeurs XLSX sans macro, CSV ou texte. 200 Mo par fichier, 20 fichiers par envoi.</p>
          </div>
          {erreursChamps.fichiers && <p className="champ-erreur" role="alert">{erreursChamps.fichiers}</p>}
          {fichiers.length > 0 && (
            <ul className="liste doc-fichiers" aria-label="Fichiers à importer">
              {fichiers.map((f) => (
                <li key={`${f.name}-${f.size}`} className="doc-fichier">
                  <span className="liste-element-corps">
                    <span className="liste-element-titre">{f.name}</span>
                    <span className="tres-petit discret">{tailleFichier(f.size)}{f.size > TAILLE_MAX ? ' — trop volumineux' : ''}</span>
                  </span>
                  <Bouton variante="fantome" className="btn-icone" aria-label={`Retirer ${f.name}`} onClick={() => setFichiers((l) => l.filter((x) => x !== f))}>
                    <Trash2 aria-hidden />
                  </Bouton>
                </li>
              ))}
            </ul>
          )}
        </Panneau>

        <Panneau titre="Identification">
          <div className="grille-form">
            <ChampChoix libelle="Type de document" required value={docType} onChange={(e) => setDocType(e.target.value)} vide="Choisir…"
              erreur={erreursChamps.docType} options={TYPES_BIBLIOTHEQUE.map((t) => ({ valeur: t, libelle: TYPES_DOCUMENT[t] }))} />
            <Champ libelle="Fabricant" value={fabricant} onChange={(e) => setFabricant(e.target.value)} list="doc-fabricants" autoComplete="off"
              aide="Laissez vide si inconnu." />
            <datalist id="doc-fabricants">{(fabricants.data ?? []).map((f) => <option key={f.nom} value={f.nom} />)}</datalist>
            <Champ libelle="Gamme" value={gamme} onChange={(e) => setGamme(e.target.value)} />
            {fichiers.length <= 1 && <Champ libelle="Titre" value={titre} onChange={(e) => setTitre(e.target.value)} aide="Par défaut : le nom du fichier." />}
          </div>
          <div className="champ" style={{ marginTop: 16 }}>
            <label className="champ-libelle" htmlFor="doc-ref-saisie">Références produit couvertes</label>
            <div className="rangee" style={{ gap: 8, flexWrap: 'nowrap' }}>
              <input id="doc-ref-saisie" className="saisie saisie-mono" value={refSaisie} onChange={(e) => setRefSaisie(e.target.value)} onKeyDown={toucheRef}
                placeholder="Saisir puis Entrée" spellCheck={false} autoComplete="off" aria-describedby="doc-ref-aide" />
              <Bouton onClick={ajouterRef} disabled={!refSaisie.trim()}>Ajouter</Bouton>
            </div>
            <span id="doc-ref-aide" className="champ-aide">Références exactes, telles qu’imprimées sur la plaque ou dans le catalogue. Plusieurs références : séparez-les par une virgule.</span>
            {refs.length > 0 && (
              <ul className="doc-puces" aria-label="Références ajoutées">
                {refs.map((r) => (
                  <li key={r} className="doc-puce"><span className="mono">{r}</span>
                    <button type="button" className="doc-puce-retirer" aria-label={`Retirer la référence ${r}`} onClick={() => setRefs((l) => l.filter((x) => x !== r))}><X aria-hidden /></button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="grille-form" style={{ marginTop: 16 }}>
            <Champ libelle="Versions couvertes" value={versionsCouvertes} onChange={(e) => setVersionsCouvertes(e.target.value)}
              aide="Matériel, logiciel ou firmware couverts, tels qu’indiqués dans le document." />
            <Champ libelle="Libellé de version du document" value={libelleVersion} onChange={(e) => setLibelleVersion(e.target.value)}
              aide="Ex. « rév. C », « indice 3 ». Par défaut : v1." />
            <Champ libelle="Date de publication" type="date" value={publication} onChange={(e) => setPublication(e.target.value)} aide="Si elle figure dans le document." />
            <ChampChoix libelle="Langue" value={langue} onChange={(e) => setLangue(e.target.value)} vide="Inconnue"
              options={Object.entries(LANGUES).map(([valeur, libelle]) => ({ valeur, libelle }))} />
            <Champ libelle="Région" value={region} onChange={(e) => setRegion(e.target.value)} aide="Ex. France, Europe." />
          </div>
          <fieldset className="doc-protocoles">
            <legend className="champ-libelle">Protocoles cités</legend>
            <div className="doc-puces">
              {PROTOCOLES.map((p) => (
                <button key={p} type="button" className="doc-bascule" aria-pressed={protocoles.includes(p)}
                  onClick={() => setProtocoles((l) => (l.includes(p) ? l.filter((x) => x !== p) : [...l, p]))}>{p}</button>
              ))}
            </div>
          </fieldset>
        </Panneau>

        <Panneau titre="Source et droits d’utilisation">
          <p className="petit secondaire" style={{ marginBottom: 12 }}>
            Obligatoire : la bibliothèque ne reçoit que des documents obtenus par une voie autorisée. Ne contournez ni un compte partenaire ni une licence.
          </p>
          <div className="grille-form">
            <ChampTexte libelle="Provenance" required value={provenance} onChange={(e) => setProvenance(e.target.value)} rows={2}
              placeholder="Ex. site du fabricant, rubrique téléchargements ; remis par le distributeur le…" erreur={erreursChamps.provenance} />
            <div className="pile" style={{ gap: 8 }}>
              <ChampChoix libelle="Droits d’utilisation" required value={droitsChoix} onChange={(e) => setDroitsChoix(e.target.value)} vide="Choisir…"
                erreur={droitsChoix === 'autre' ? null : erreursChamps.rights}
                options={[...DROITS_PROPOSES.map((d) => ({ valeur: d, libelle: d })), { valeur: 'autre', libelle: 'Autres conditions (préciser)' }]} />
              {droitsChoix === 'autre' && <Champ libelle="Conditions d’utilisation" value={droitsLibre} onChange={(e) => setDroitsLibre(e.target.value)} erreur={erreursChamps.rights} />}
            </div>
          </div>
        </Panneau>

        {erreur && <Alerte ton="danger" titre="Import impossible">{erreur}</Alerte>}
        <div className="doc-barre-actions">
          <span className="petit secondaire">{fichiers.length ? pluriel(fichiers.length, 'fichier prêt', 'fichiers prêts') : 'Aucun fichier sélectionné'}</span>
          <Bouton type="submit" variante="principal" icone={<Upload aria-hidden />} chargement={envoi}>
            {envoi ? 'Envoi en cours…' : 'Importer'}
          </Bouton>
        </div>
      </form>
    </div>
  );
}

// ─── Suivi des fichiers envoyés : tâche de lecture puis statut de la version ───
function SuiviImport({ resultats }: { resultats: Resultat[] }) {
  const ok = resultats.filter((r) => !r.erreur).length;
  return (
    <Panneau serre titre="Fichiers envoyés" actions={<span className="petit secondaire">{ok} sur {resultats.length} accepté{ok > 1 ? 's' : ''}</span>}>
      <ul className="liste" aria-live="polite">
        {resultats.map((r, i) => <LigneSuivi key={`${r.nom}-${i}`} r={r} />)}
      </ul>
    </Panneau>
  );
}

interface TacheSuivie { id: string; status: string; progress: number; progress_label: string | null; error: string | null }

function LigneSuivi({ r }: { r: Resultat }) {
  const actif = !r.erreur && !r.doublon && !!r.versionId;
  const [fini, setFini] = useState(false);
  const tache = useQuery({
    queryKey: ['taches', r.versionId], enabled: actif && !fini,
    queryFn: () => api.get<TacheSuivie[]>(`/api/taches?entity_id=${r.versionId}`),
    refetchInterval: 1000,
  });
  const t = tache.data?.[0];
  const termine = !!t && ['termine', 'echec', 'annule'].includes(t.status);
  useEffect(() => { if (termine) setFini(true); }, [termine]);
  const version = useQuery({
    queryKey: ['version', r.versionId, fini], enabled: !!r.versionId && (fini || !!r.doublon),
    queryFn: () => api.get<{ version: { status: string; status_detail: string | null } }>(`/api/documents/versions/${r.versionId}`),
  });

  return (
    <li className="liste-element doc-suivi">
      {r.erreur ? <XCircle aria-hidden className="doc-icone-danger" /> : <CheckCircle2 aria-hidden className="doc-icone-ok" />}
      <span className="liste-element-corps">
        <span className="liste-element-titre">{r.titre ?? r.nom}</span>
        {r.titre && r.titre !== r.nom && <span className="tres-petit discret">{r.nom}</span>}
        {r.erreur && <span className="champ-erreur" role="alert">{r.erreur}</span>}
        {r.doublon && <span className="petit"><Badge ton="info">Doublon : déjà présent</Badge> Ce fichier est déjà dans la bibliothèque ; rien n’a été ajouté.</span>}
        {actif && !fini && t && <Progression pct={t.progress} libelle={t.progress_label ?? 'En file d’attente'} />}
        {actif && !fini && !t && tache.isLoading && <span className="tres-petit discret">Suivi du traitement…</span>}
        {t?.status === 'echec' && <span className="champ-erreur">{t.error ?? 'Le traitement a échoué.'}</span>}
        {version.data?.version.status_detail && <span className="petit secondaire">{version.data.version.status_detail}</span>}
      </span>
      <span className="doc-suivi-etat">
        {t && !fini && <BadgeStatutTache statut={t.status} />}
        {version.data && <BadgeStatutDocument statut={version.data.version.status} />}
        {r.documentId && <Link className="btn btn-compact" to={`/documentation/${r.documentId}`}>Voir le document</Link>}
      </span>
    </li>
  );
}
